/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import { getBillingTypeLabel } from '@sportninja/common/utils/billing'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { formatRegistrationMoney } from '../../utils/formatRegistrationMoney'
import {
  getApplicableBilling,
  getRegistrationCountry,
} from '../../utils/registrationRows'

const overlayStyle = { maxWidth: 460 }
const overlayInnerStyle = { padding: 12 }

/** The first payment's date; several payments list out in a tooltip. */
const PaidDateCell = ({
  data: record,
}: ICellRendererParams<UserRegistration>) => {
  if (!record) {
    return null
  }

  const billing = getApplicableBilling(record)
  const paidItems = billing.filter((item) => item.paid && item.paid_at)

  if (paidItems.length === 0) {
    return (
      <Text variant='paragraphSmall' weight='regular' color={colors.NEUTRAL_50}>
        --
      </Text>
    )
  }

  const firstDate = formatShortDate(paidItems[0].paid_at as string, false)

  if (paidItems.length === 1) {
    return (
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        {firstDate}
      </Text>
    )
  }

  const country = getRegistrationCountry(record)

  return (
    <Tooltip
      placement='topRight'
      overlayStyle={overlayStyle}
      overlayInnerStyle={overlayInnerStyle}
      title={
        <table css={breakdownStyle}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Due Date</th>
              <th>Paid Date</th>
              <th>Paid</th>
            </tr>
          </thead>
          <tbody>
            {billing.map((item) => (
              <tr key={item.uid}>
                <td>{getBillingTypeLabel(item)}</td>
                <td>{formatShortDate(item.due_date, false)}</td>
                <td>
                  {item.paid && item.paid_at
                    ? formatShortDate(item.paid_at, false)
                    : '--'}
                </td>
                <td>
                  {item.paid
                    ? formatRegistrationMoney(item.display_subtotal, country)
                    : '--'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div css={triggerStyle}>
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {firstDate}
        </Text>
        <span css={moreStyle}>+{paidItems.length - 1}</span>
      </div>
    </Tooltip>
  )
}

export default PaidDateCell

// ─── Styles ──────────────────────────────────────────────────────

const breakdownStyle = css`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    ${paragraphXSmall}
    padding: 4px 8px;
    text-align: left;
    white-space: nowrap;
  }
  th {
    ${weightSemiBold}
    border-bottom: 1px solid ${colors.NEUTRAL_100};
    color: ${colors.NEUTRAL_50};
  }
  td {
    color: ${colors.WHITE};
  }
`

const triggerStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`

const moreStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${colors.PRIMARY};
`
